<template>
  <BasePopUp v-if="showModal" @detailsClosed="closeConfirmRequested">
    <div v-if="order" class="modal-body-container">
      <!-- Title -->
      <h4 class="text-center mt-4">{{ $t("orderDetail.title") }} #{{order.id}}</h4>
      <div class="d-flex flex-column px-3">
        <!-- Editable information (status, transporter url, shipping date) -->
        <form class="d-flex flex-column">
          <!-- Status -->
          <div role="group" class="form-group flex-grow-1">
            <label class="m-0"> {{ $t('status') }}</label>
            <SingleOptionDropdown v-model="status" :options="statusOptions" :title="$t('status')" :index="0"></SingleOptionDropdown>
          </div>
          <!-- Transporter url -->
          <div role="group" class="form-group flex-grow-1">
            <label class="m-0"> {{ $t('transporterUrl') }}</label>
            <CInput class="m-0" type="text" v-model="transporterUrl"></CInput>
          </div>
          <!-- Expected shipping date -->
          <div role="group" class="form-group flex-grow-1">
            <label class="m-0"> {{ $t('expectedShippingDate') }}</label>
            <CInput class="m-0" type="date" v-model="expectedShippingDate"></CInput>
          </div>
          <!-- Action button + submitting -->  
          <CSpinner v-if="submitting" variant="success" small></CSpinner>
          <CButton v-else type="button" class="btn btn-primary" @click="submitClicked">{{ $t('acceptChanges') }}</CButton>
        </form>
        <!-- User information (user + addresses) -->
        <div class="d-flex gap-1">
          <h5 class="my-3 font-weight-bold">{{$t("userInfo")}}</h5>
          <AccordionArrow v-model="showAddresses"></AccordionArrow>
        </div>
        <p><span class="font-weight-bold">{{ $t("orderDetail.name") }}: </span>{{ order.user.email }}</p>
        <p><span class="font-weight-bold">{{ $t("orderDetail.email") }}: </span>{{ order.user.name }}</p>
        <p v-if="order.iban"><span class="font-weight-bold">{{ $t("orderDetail.iban") }}: </span>{{ order.iban}}</p>
        <div id="address-box" v-if="showAddresses" class="d-flex flex-wrap justify-content-between p-3">
          <div v-if="order.addresses[0]">
            <h5 class="mb-4 text-left font-weight-bold">{{ $t("address.invoice") }}</h5>
            <p v-if="order.addresses[0].name" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.name") }}: </span>{{order.addresses[0].name}}</p>
            <p v-if="order.addresses[0].email" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.email") }}: </span>{{order.addresses[0].email}}</p>
            <p v-if="order.addresses[0].phone" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.phone") }}: </span>{{order.addresses[0].phone}}</p>
            <p v-if="order.addresses[0].companyName" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.company") }}: </span>{{order.addresses[0].companyName}}</p>
            <p v-if="order.addresses[0].vat" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.nif") }}: </span>{{order.addresses[0].vat}}</p>
            <p v-if="order.addresses[0].country" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.country") }}: </span>{{order.addresses[0].country}}</p>
            <p v-if="order.addresses[0].street" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.address") }}: </span>{{order.addresses[0].street}}</p>
            <p v-if="order.addresses[0].zipCode" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.zip") }}: </span>{{order.addresses[0].zipCode}}</p>
            <p v-if="order.addresses[0].state" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.location") }}: </span>{{order.addresses[0].state}}</p>
            <p v-if="order.addresses[0].city" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.city") }}: </span>{{order.addresses[0].city}}</p>
          </div>
          <div v-if="order.addresses[1]">
            <h5 class="mb-4 text-left font-weight-bold">{{ $t("address.shipping") }}</h5>
            <p v-if="order.addresses[1].name" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.name") }}: </span>{{order.addresses[1].name}}</p>
            <p v-if="order.addresses[1].email" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.email") }}: </span>{{order.addresses[1].email}}</p>
            <p v-if="order.addresses[1].phone" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.phone") }}: </span>{{order.addresses[1].phone}}</p>
            <p v-if="order.addresses[1].companyName" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.company") }}: </span>{{order.addresses[1].companyName}}</p>
            <p v-if="order.addresses[1].vat" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.nif") }}: </span>{{order.addresses[1].vat}}</p>
            <p v-if="order.addresses[1].country" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.country") }}: </span>{{order.addresses[1].country}}</p>
            <p v-if="order.addresses[1].street" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.address") }}: </span>{{order.addresses[1].street}}</p>
            <p v-if="order.addresses[1].zipCode" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.zip") }}: </span>{{order.addresses[1].zipCode}}</p>
            <p v-if="order.addresses[1].state" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.location") }}: </span>{{order.addresses[1].state}}</p>
            <p v-if="order.addresses[1].city" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.city") }}: </span>{{order.addresses[1].city}}</p>
          </div>
        </div>
        <!-- Product information -->
        <h5 class="my-3 font-weight-bold">{{$t("products")}}</h5>
        <div class="row">
          <span class="font-weight-bold col-3">{{ $t("orderDetail.sku") }} </span>
          <span class="font-weight-bold col-3">{{ $t("orderDetail.name") }} </span>
          <span class="font-weight-bold col-3">{{ $t("orderDetail.quantity") }} </span>
          <span class="font-weight-bold col-3">{{ $t("orderDetail.price") }}</span>
        </div>
        <div class="row" v-for="(product, index) in order.products" :key="`product-${index}`">
          <span class="col-3">{{ product.sku }}</span>
          <span class="col-3">{{ product.name }}</span>
          <span class="col-3">{{ product.quantity }}</span>
          <div class="d-flex col-3 justify-content-between align-items-center">
            <span>{{ product.price }} €</span>
            <AccordionArrow v-if="Object.keys(product.colors).length || Object.keys(product.images).length || product.notes != null" v-model="selectedProduct" :selectedValue="product"></AccordionArrow>
          </div>
          <div v-if="Object.keys(product.colors).length || Object.keys(product.images).length || product.notes != null" class="col-12 text-right px-5 mt-3">
            <div v-show="selectedProduct == product">
              <span class="font-weight-bold"> {{ $t("orderDetail.openSimulator") }} </span>
              <span class="cursor-pointer" :style="{textDecoration: 'underline'}" @click="goToSimulator(order.id,index)"> {{ $t("orderDetail.ViewProduct") }} </span>
            </div>
            <!-- colors -->
            <div v-if="!Object.keys(product.colors).length" v-show="selectedProduct == product" class="col-12 p-0 mt-2 mb-2">
              <span class="font-weight-bold">{{ $t("orderDetail.colors") }}: </span>
              {{ $t("orderDetail.noColors") }}
            </div>
            <div v-else v-show="selectedProduct == product" class="col-12 p-0" >
              <div class="py-1">
                <span class="font-weight-bold">{{ $t("orderDetail.colors") }}: </span>
              </div>
              <div class="d-flex justify-content-between py-3 flex-wrap">
                <div class="py-2" v-for="(value, key) in colors" :key="key">
                  <span>{{key}} - </span>
                  <span :style="{color: value}">{{ value }}</span>
                </div>
              </div>
            </div>
            <!-- images -->
            <div v-if="!Object.keys(product.images).length" v-show="selectedProduct == product" class="col-12 p-0 mt-2 mb-2">
              <span class="font-weight-bold">{{ $t("orderDetail.images") }}: </span>
              {{ $t("orderDetail.noImages") }}
            </div>
            <div v-else v-show="selectedProduct == product" class="col-12 p-0" >
              <div class="py-1">
                <span class="font-weight-bold">{{ $t("orderDetail.images") }}: </span>
              </div>
              <div class="d-flex justify-content-between py-3 flex-wrap">
                <div class="py-2" v-for="(value, key) in images" :key="key">
                  <span>{{key}} - </span>
                  <a class="cursor-pointer" :style="{textDecoration: 'underline'}" @click="convertBase64ToImage(value)">Download</a>
                </div>
              </div>
            </div>
            <div v-show="selectedProduct == product" class="col-12 p-0"><span class="font-weight-bold">{{ $t("orderDetail.notes") }}: </span>{{ product.notes ? product.notes : $t("orderDetail.noNotes") }}</div>
          </div>
          <hr v-if="index != order.products.length - 1" class="w-100 mt-3"/>
        </div>
        <!-- Cart value -->
        <h5 class="my-3 font-weight-bold">{{$t("cartBalance")}}</h5>
        <div class="row">
          <span class="font-weight-bold col-3">{{ $t("orderDetail.subTotal") }}</span>
          <span class="font-weight-bold col-3">{{ $t("orderDetail.discount") }}</span>
          <span class="font-weight-bold col-3">{{ $t("orderDetail.taxes") }}</span>
          <span class="font-weight-bold col-3">{{ $t("orderDetail.total") }}</span>
        </div>
        <div class="row">
          <span class="col-3">{{ order.subtotal }} €</span>
          <span class="col-3">{{ order.discount }} €</span>
          <span class="col-3">{{ order.taxes }} €</span>
          <span class="col-3">{{ order.total }} €</span>
        </div>
        <div v-if="order.notes.length"><span class="font-weight-bold">{{ $t("orderDetail.notes") }}: </span>{{ order.notes[0].description != "" ? order.notes[0].description : $t("orderDetail.noNotes") }}</div>
        <!-- Status history -->
        <h5 class="my-3 font-weight-bold">{{$t("statusHistory")}}</h5>
        <div class="d-flex flex-wrap mb-2 justify-content-between" v-for="(status, index) in order.statuses" :key="status.date">
          <p><span class="font-weight-bold">{{ $t("orderDetail.status") }}: </span>{{$t(`orderStatus.${convertToCamelCase(status.status)}`)}}</p>
          <p><span class="font-weight-bold">{{ $t("orderDetail.date") }}: </span>{{formatDateTime(status.date)}}</p>
          <hr v-if="index != order.statuses.length - 1" class="w-100 mt-2"/>
        </div>
      </div>
      <!-- Button -->
      <div class="d-flex justify-content-center mt-5">
        <CButton type="button" class="btn btn-primary m-auto" @click="closeConfirmRequested">{{ $t("close") }}</CButton>
      </div>
    </div>
  </BasePopUp>
</template>

<script>
import BasePopUp from "./BasePopUp.vue";
import { convertToCamelCase, formatDate, formatDateTime } from "@/helpers/utils.js";
import { mapState, mapActions } from 'vuex'
import SingleOptionDropdown from '@/controls/SingleOptionDropdown.vue'
import AccordionArrow from '@/controls/AccordionArrow.vue'
import { API_HELPER } from "@/helpers/api.js";

export default {
  extends: BasePopUp,
  components: { BasePopUp, SingleOptionDropdown, AccordionArrow },
  props: {
    order: Object,
  },
  data() {
    return {
      status: "PendingPayment",
      transporterUrl: null,
      expectedShippingDate: null,
      colors: {},
      images: {},
      statusOptions: [{label: this.$t("orderStatus.pendingPayment"), key: "PendingPayment"}, {label: this.$t("orderStatus.pendingProcessing"), key: "PendingProcessing"}, {label: this.$t("orderStatus.processing"), key: "Processing"}, {label: this.$t("orderStatus.readyToShip"), key: "ReadyToShip"}, {label: this.$t("orderStatus.shipped"), key: "Shipped"}, {label: this.$t("orderStatus.concluded"), key: "Concluded"}, {label: this.$t("orderStatus.canceled"), key: "Canceled"} ],
      showAddresses: false,
      selectedProduct: null,
    };
  },
  computed:
  {
    ...mapState('orders', ['putOrderOutput']),
    submitting: function()
    {
      return this.putOrderOutput === null;
    },
  },
  methods: {
    convertBase64ToImage(base64)
    {
      let base = 'data:image/jpeg;base64,' + base64
      // convert base64 to raw binary data held in a string
      // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
      let byteString = atob(base.split(',')[1]);

      // separate out the mime component
      let mimeString = base.split(',')[0].split(':')[1].split(';')[0]

      // write the bytes of the string to an ArrayBuffer
      let ab = new ArrayBuffer(byteString.length);

      // create a view into the buffer
      let ia = new Uint8Array(ab);

      // set the bytes of the buffer to the correct values
      for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
      }

      // write the ArrayBuffer to a blob, and you're done
      let blob = new Blob([ab], {type: mimeString});
      let fileSaver = require('file-saver');
      fileSaver.saveAs(blob);

    },
    closeConfirmRequested() {
      this.$emit("closeConfirmRequested");
    },
    formatDateTime,
    ...mapActions('orders', ['putOrder']),
    submitClicked()
    {
      const {status, transporterUrl, expectedShippingDate} = this;
      const id = this.order.id;

      this.putOrder({id, status, transporterUrl, expectedShippingDate});
    },
    goToSimulator(orderId, index)
    {
      var fullUrl = encodeURI(API_HELPER.pathJoin([process.env.VUE_APP_FRONT, `?id=${orderId}&index=${index}`]));
      window.open(fullUrl, '_blank');
    },
    convertToCamelCase
  },
  watch:
  {
    selectedProduct(val)
    {
      if(val != null)
      {
        //colors
        Object.assign(this.colors, val.colors);
        //images
        Object.assign(this.images, val.images);
      }
    },
    order: function(val)
    {
      if(val)
      {
        this.status = this.order.statuses[this.order.statuses.length - 1].status;
        this.expectedShippingDate = formatDate(this.order.expectedShippingDate, "YYYY-MM-DD");
        this.transporterUrl = this.order.transporterUrl;
      }
    },
    putOrderOutput: function(val)
    {
      if(val)
      {
        this.$emit("update-order", this.order.id);
      }
    }
  }
};
</script>

<style scoped>
.modal-body-container {
  background-color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 10px;
  border-radius: 10px;
  min-height: 200px;
  min-width: 620px;
}

#address-box
{
  border: 1px solid lightslategray;
}
</style>